import apiClient from '../apiClient';
import API_CONFIG from '../config';

/**
 * Service for handling appointments
 */
const appointmentService = {
  /**
   * Get all appointments with optional filters
   * @param {Object} params - Query parameters (date, patient, status, etc.)
   * @returns {Promise} - List of appointments
   */
  async getAppointments(params = {}) {
    try {
      return await apiClient.get(API_CONFIG.ENDPOINTS.APPOINTMENTS.LIST, params);
    } catch (error) {
      console.error('Error fetching appointments:', error);
      throw error;
    }
  },
  
  /**
   * Get a single appointment by ID
   * @param {string} id - Appointment ID
   * @returns {Promise} - Appointment details
   */
  async getAppointment(id) {
    try {
      return await apiClient.get(API_CONFIG.ENDPOINTS.APPOINTMENTS.DETAILS(id));
    } catch (error) {
      console.error(`Error fetching appointment with ID ${id}:`, error);
      throw error;
    }
  },
  
  /**
   * Create a new appointment
   * @param {Object} appointmentData - Appointment data
   * @returns {Promise} - Created appointment
   */
  async createAppointment(appointmentData) {
    try {
      return await apiClient.post(API_CONFIG.ENDPOINTS.APPOINTMENTS.CREATE, appointmentData);
    } catch (error) {
      console.error('Error creating appointment:', error);
      throw error;
    }
  },
  
  /**
   * Update an existing appointment
   * @param {string} id - Appointment ID
   * @param {Object} appointmentData - Updated appointment data
   * @returns {Promise} - Updated appointment
   */
  async updateAppointment(id, appointmentData) {
    try {
      return await apiClient.put(API_CONFIG.ENDPOINTS.APPOINTMENTS.UPDATE(id), appointmentData);
    } catch (error) {
      console.error(`Error updating appointment with ID ${id}:`, error);
      throw error;
    }
  },
  
  /**
   * Delete an appointment
   * @param {string} id - Appointment ID
   * @returns {Promise} - Success message
   */
  async deleteAppointment(id) {
    try {
      return await apiClient.delete(API_CONFIG.ENDPOINTS.APPOINTMENTS.DELETE(id));
    } catch (error) {
      console.error(`Error deleting appointment with ID ${id}:`, error);
      throw error;
    }
  },
  
  /**
   * Optimize appointment scheduling with AI
   * @param {Object} params - Optimization parameters
   * @returns {Promise} - Optimized schedule
   */
  async optimizeSchedule(params = {}) {
    try {
      return await apiClient.post(API_CONFIG.ENDPOINTS.APPOINTMENTS.OPTIMIZE, params);
    } catch (error) {
      console.error('Error optimizing schedule:', error);
      throw error;
    }
  },
  
  /**
   * Get available time slots for booking
   * @param {Object} params - Query parameters (date, provider, duration)
   * @returns {Promise} - List of available time slots
   */
  async getAvailableTimeSlots(params = {}) {
    try {
      return await apiClient.get(API_CONFIG.ENDPOINTS.APPOINTMENTS.LIST, {
        ...params,
        available: true,
      });
    } catch (error) {
      console.error('Error fetching available time slots:', error);
      throw error;
    }
  },
  
  /**
   * Send appointment reminder
   * @param {string} id - Appointment ID
   * @param {string} type - Reminder type (email, sms)
   * @returns {Promise} - Success message
   */
  async sendReminder(id, type = 'email') {
    try {
      return await apiClient.post(`${API_CONFIG.ENDPOINTS.APPOINTMENTS.DETAILS(id)}/reminders`, {
        type,
      });
    } catch (error) {
      console.error(`Error sending ${type} reminder for appointment ${id}:`, error);
      throw error;
    }
  },
  
  /**
   * Get appointment statistics
   * @param {Object} params - Query parameters (startDate, endDate)
   * @returns {Promise} - Appointment statistics
   */
  async getStatistics(params = {}) {
    try {
      return await apiClient.get(API_CONFIG.ENDPOINTS.ANALYTICS.APPOINTMENTS, params);
    } catch (error) {
      console.error('Error fetching appointment statistics:', error);
      throw error;
    }
  },
};

export default appointmentService; 